import { Tag, TagAssignResponse, TagCreateInput, TagUpdateInput } from '@vivport/backend/types';
import { httpClient } from './http-client';

/**
 * Tag API
 * Type-safe wrappers for all tag management endpoints
 * @requires Authentication
 */

// ============================================================================
// Tag Operations
// ============================================================================

/**
 * Get all non-system tags for the member's hub
 */
export async function listTags(): Promise<Tag[]> {
  return httpClient.get<Tag[]>('/protected/tags/');
}

/**
 * Get tag detail by ID
 */
export async function getTag(id: number): Promise<Tag> {
  return httpClient.get<Tag>(`/protected/tags/${id}`);
}

export async function createTag(data: TagCreateInput): Promise<Tag> {
  return httpClient.post<Tag, TagCreateInput>('/protected/tags', data);
}

export async function updateTag(id: number, data: TagUpdateInput): Promise<Tag> {
  return httpClient.patch<Tag, TagUpdateInput>(`/protected/tags/${id}`, data);
}

export async function deleteTag(id: number): Promise<void> {
  return httpClient.delete<void>(`/protected/tags/${id}`);
}

export interface TagReorderPayload {
  tagIds: number[];
  tagGroupId?: number | null;
}

export async function reorderTags(payload: TagReorderPayload): Promise<void> {
  return httpClient.post<void, TagReorderPayload>('/protected/tags/reorder', payload);
}


// ============================================================================
// Tag Content Assignment
// ============================================================================

export interface TagContentInput {
  contentIds: number[];
}

/**
 * Assign tag to multiple content items
 */
export async function assignTagToContent(tagId: number, contentIds: number[]): Promise<TagAssignResponse> {
  return httpClient.post<TagAssignResponse, TagContentInput>(`/protected/tags/${tagId}/assign`, { contentIds });
}

/**
 * Remove tag from multiple content items
 */
export async function removeTagFromContent(tagId: number, contentIds: number[]): Promise<Tag> {
  return httpClient.post<Tag, TagContentInput>(`/protected/tags/${tagId}/remove`, { contentIds });
}

/**
 * Get all content IDs associated with a tag
 */
export async function getTagContents(tagId: number): Promise<{ ids: number[] }> {
  return httpClient.get<{ ids: number[] }>(`/protected/tags/${tagId}/contents`);
}

/**
 * Get the count of content items tagged with a specific tag
 */
export async function getTagContentsCount(tagId: number): Promise<{ count: number }> {
  const result = await getTagContents(tagId);
  return { count: result.ids.length };
}

/**
 * Get count of how many selected content items have a specific tag
 */
export async function getTagContentsSelectedCount(tagId: number, contentIds: number[]): Promise<{ count: number }> {
  const result = await getTagContents(tagId);
  const filteredIds = result.ids.filter(id => contentIds.includes(id));
  return { count: filteredIds.length };
}

